'use client';

import React, { useMemo, useState } from 'react';
import { CardWrapperProps } from '@/components/cards-wrapper/types';
import Card from '@/components/card/Card';
import ChipsWrapper from '@/components/chips/ChipsWrapper';
import { motion } from 'framer-motion';
import { HeadlineWrapper } from '@/components/headline-wrapper/HeadlineWrapper';
import clsx from 'clsx';
import { renameDomProps } from '@/utils/sanitizeDomProps';

const CardWrapper: React.FC<CardWrapperProps> = ({
  cards,
  className,
  sectionBackgroundColor,
  headlineWrapper,
  cardAlignment = 'left',
  sectionId,
  ...rest
}) => {
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const renamedProps = renameDomProps(rest);

  const uniqueTags = useMemo(() => {
    const tags = Array.from(
      new Set(cards.map((card) => card.tag).filter(Boolean) as string[]),
    );
    return tags.sort((a, b) => a?.localeCompare(b));
  }, [cards]);

  const handleTagSelectionChange = (tag: string | null) => {
    setSelectedTag(tag === selectedTag ? null : tag);
  };

  const filteredCards = useMemo(
    () =>
      selectedTag ? cards.filter((card) => card.tag === selectedTag) : cards,
    [cards, selectedTag],
  );

  return (
    <div
      id={sectionId}
      className={`relative w-full py-12 ${className} ${
        sectionBackgroundColor ? `bg-${sectionBackgroundColor}` : ''
      }`}
      {...renamedProps}
    >
      <div
        className={`${className} card-wrapper container relative z-10 mx-auto flex flex-col gap-10 px-4 sm:px-0`}
      >
        {headlineWrapper && (
          <HeadlineWrapper {...headlineWrapper} position={cardAlignment} />
        )}
        {uniqueTags.length > 0 && (
          <div
            className={clsx(
              'flex flex-col gap-6',
              cardAlignment === 'center' ? 'mx-auto' : '',
            )}
          >
            <ChipsWrapper
              options={uniqueTags}
              selectedOptions={selectedTag ? [selectedTag] : []}
              onSelectionChange={(tags) =>
                handleTagSelectionChange(tags[0] || null)
              }
              variant="inline-wrap"
              selectionMode="single"
            />
          </div>
        )}
        <div className="grid grid-cols-6 gap-6 md:grid-cols-12">
          {filteredCards.map((cardProps, index) => (
            <motion.div
              key={`card-${cardProps.title}-${index}`}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="col-span-full h-full sm:col-span-6 lg:col-span-4"
              transition={{ ease: 'easeInOut', duration: 0.5 }}
              layout
            >
              <div className="card-item h-full self-stretch">
                <Card {...cardProps} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CardWrapper;
